import React, { useState, useEffect } from 'react';
import { FiCreditCard, FiCheck, FiDownload, FiCalendar, FiX, FiAlertTriangle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSubscription, getPlanName, getStatusBadge } from '../../hooks/useSubscription';
import { getPublicPlans, PublicPlan, getOrganizationStatus, getPendingDowngrade, cancelPendingDowngrade } from '../../api/subscriptions';
import StripeCheckout from './StripeCheckout';
import { createCustomerPortalSession, getInvoices, Invoice, cancelSubscription, previewCancellation, QuotaPrediction, submitCancellationFeedback, applyRetentionOffer } from '../../api/payments';
import { useToast } from '../../context/ToastContext';
import { StatusBanner, BannerType } from './StatusBanner';
import DowngradeWarningModal from './DowngradeWarningModal';
import DowngradeConfirmationModal from './DowngradeConfirmationModal';
import CancellationFlowModal from './CancellationFlowModal';
import { useCurrency } from '../../contexts/CurrencyContext';
import CurrencySelector from '../common/CurrencySelector';

const SubscriptionPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const toast = useToast();
  const { subscription, loading: subLoading, error: subError } = useSubscription();
  const { currency, formatAmount } = useCurrency();
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [suggestedPlan, setSuggestedPlan] = useState<'free' | 'standard' | 'entreprise'>('standard');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [downgradingToFree, setDowngradingToFree] = useState(false);

  // États pour les bannières de statut
  const [organizationStatus, setOrganizationStatus] = useState<any>(null);
  const [pendingDowngrade, setPendingDowngrade] = useState<any>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // États pour Modal A (Confirmation downgrade) et Modal B (Avertissement dépassement)
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [previewData, setPreviewData] = useState<{
    quota_prediction: QuotaPrediction;
    current_plan: string;
    target_plan: string;
    effective_date: string | null;
  } | null>(null);

  // État pour le flow d'annulation amélioré
  const [showCancellationFlow, setShowCancellationFlow] = useState(false);

  // Charger les plans publics
  useEffect(() => {
    const loadPlans = async () => {
      try {
        const data = await getPublicPlans();
        setPlans(data);
        setPlansLoading(false);
      } catch (error: any) {
        console.error('Failed to load plans:', error);
        setPlansError(error.message || 'Failed to load plans');
        setPlansLoading(false);
      }
    };

    loadPlans();
  }, []);

  // Charger les factures si plan payant
  useEffect(() => {
    const loadInvoices = async () => {
      if (subscription && subscription.plan !== 'free') {
        setInvoicesLoading(true);
        try {
          const data = await getInvoices(10);
          setInvoices(data.invoices);
        } catch (error) {
          console.error('Failed to load invoices:', error);
        } finally {
          setInvoicesLoading(false);
        }
      }
    };

    loadInvoices();
  }, [subscription]);

  // Charger le statut de l'organisation et les downgrades programmés
  useEffect(() => {
    const loadStatusAndDowngrade = async () => {
      setStatusLoading(true);
      try {
        // Charger le statut de l'organisation
        const statusData = await getOrganizationStatus();
        setOrganizationStatus(statusData);

        // Charger les downgrades programmés
        const downgradeData = await getPendingDowngrade();
        setPendingDowngrade(downgradeData);
      } catch (error) {
        console.error('Failed to load organization status:', error);
      } finally {
        setStatusLoading(false);
      }
    };

    if (subscription) {
      loadStatusAndDowngrade();
    }
  }, [subscription]);

  const handleSelectPlan = async (planCode: string) => {
    if (subscription && planCode !== subscription.plan) {
      // Pour les plans payants (standard, entreprise), ouvrir Stripe Checkout
      if (planCode === 'standard' || planCode === 'entreprise') {
        setSuggestedPlan(planCode as 'standard' | 'entreprise');
        setShowStripeCheckout(true);
      } else if (planCode === 'free') {
        // Pour downgrade vers free, précharger les données et afficher le flow d'annulation amélioré
        try {
          const preview = await previewCancellation();
          setPreviewData(preview);
          setShowCancellationFlow(true);
        } catch (error: any) {
          console.error('Failed to preview cancellation:', error);
          toast.error('Impossible de charger les informations de downgrade.');
        }
      }
    }
  };

  const handleDowngradeToFree = async () => {
    if (!previewData) {
      toast.error('Erreur: données de preview manquantes.');
      return;
    }

    setDowngradingToFree(true);
    try {
      // Si dépassement détecté, afficher Modal B (Warning)
      if (previewData.quota_prediction.will_exceed_quotas) {
        setShowDowngradeModal(false); // Fermer Modal A
        setShowWarningModal(true); // Ouvrir Modal B
        setDowngradingToFree(false);
        return;
      }

      // Pas de dépassement → appliquer directement le downgrade
      await confirmDowngrade();
    } catch (error: any) {
      console.error('Failed to process downgrade:', error);
      toast.error(error.response?.data?.message || 'Impossible de traiter le downgrade.');
      setDowngradingToFree(false);
    }
  };

  // Confirmer et appliquer le downgrade (appelé soit directement, soit depuis Modal B)
  const confirmDowngrade = async () => {
    try {
      setDowngradingToFree(true);
      const result = await cancelSubscription();

      toast.success('Votre abonnement sera annulé à la fin de la période de facturation. Vous repasserez automatiquement au plan gratuit.');

      setShowDowngradeModal(false);
      setShowWarningModal(false);

      // Rafraîchir après 2 secondes
      setTimeout(() => window.location.reload(), 2000);
    } catch (error: any) {
      console.error('Failed to cancel subscription:', error);
      toast.error(error.response?.data?.message || 'Impossible d\'annuler l\'abonnement.');
      setDowngradingToFree(false);
    }
  };

  const handleOpenCustomerPortal = async () => {
    try {
      const returnUrl = window.location.href;
      const { url } = await createCustomerPortalSession(returnUrl);
      window.location.href = url;
    } catch (error: any) {
      console.error('Failed to open customer portal:', error);
      toast.error('Impossible d\'ouvrir le portail client. Veuillez réessayer.');
    }
  };

  const handleCancelDowngrade = async () => {
    try {
      const result = await cancelPendingDowngrade();
      toast.success('Votre changement de plan a été annulé. Vous restez sur votre plan actuel.');
      // Rafraîchir les données
      setPendingDowngrade(null);
      setTimeout(() => window.location.reload(), 1500);
    } catch (error: any) {
      console.error('Failed to cancel downgrade:', error);
      toast.error(error.response?.data?.message || 'Impossible d\'annuler le changement de plan.');
    }
  };

  // Handler pour la confirmation d'annulation avec feedback
  const handleConfirmCancellation = async (reason: string, feedback: string) => {
    try {
      // Soumettre le feedback
      await submitCancellationFeedback(reason, feedback);

      // Vérifier si dépassement de quotas
      if (previewData?.quota_prediction.will_exceed_quotas) {
        setShowCancellationFlow(false);
        setShowWarningModal(true);
        return;
      }

      // Procéder à l'annulation
      await cancelSubscription();
      toast.success('Votre abonnement sera annulé à la fin de la période de facturation.');
      setShowCancellationFlow(false);
      setTimeout(() => window.location.reload(), 2000);
    } catch (error: any) {
      console.error('Failed to cancel:', error);
      toast.error(error.response?.data?.message || 'Impossible d\'annuler l\'abonnement.');
      throw error;
    }
  };

  // Handler pour les offres de rétention
  const handleAcceptRetentionOffer = async (offerType: 'discount' | 'pause' | 'downgrade') => {
    try {
      const result = await applyRetentionOffer(offerType);

      if (offerType === 'discount') {
        toast.success('Réduction appliquée ! Vous bénéficiez de 20% sur votre prochain mois.');
      } else if (offerType === 'pause') {
        toast.success('Votre abonnement est en pause pour 30 jours.');
      } else if (offerType === 'downgrade') {
        toast.success('Votre changement vers le plan Standard a été programmé.');
      }

      setShowCancellationFlow(false);
      setTimeout(() => window.location.reload(), 2000);
    } catch (error: any) {
      console.error('Failed to apply retention offer:', error);
      toast.error(error.response?.data?.message || 'Impossible d\'appliquer l\'offre.');
      throw error;
    }
  };

  const formatPrice = (plan: PublicPlan): string => {
    // BUSINESS : ne JAMAIS afficher de prix
    if (!plan.display_price || plan.code === 'business') {
      return t('subscription.price.contactUs');
    }

    if (plan.price_monthly_cents === 0) {
      return t('subscription.price.free');
    }

    const priceMonthly = (plan.price_monthly_cents || 0) / 100;
    return formatAmount(priceMonthly, currency);
  };

  const getPriceSuffix = (plan: PublicPlan): string => {
    if (!plan.display_price || plan.code === 'business' || plan.price_monthly_cents === 0) {
      return '';
    }
    return '/mois';
  };

  if (subLoading || plansLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-96 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (subError || plansError) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <p className="text-red-800">Erreur: {subError || plansError}</p>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="p-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <p className="text-yellow-800">Aucune souscription trouvée</p>
        </div>
      </div>
    );
  }

  const currentPlan = subscription.plan;
  const statusBadge = getStatusBadge(subscription.status);

  return (
    <div className="p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{t('subscription.header.title', 'Abonnement')}</h2>
        <p className="text-gray-600 mt-1">
          Plan actuel : <span className="font-semibold text-blue-600">{getPlanName(currentPlan)}</span>
          <span className={`ml-3 px-3 py-1 text-xs font-medium rounded-full bg-${statusBadge.color}-100 text-${statusBadge.color}-700`}>
            {statusBadge.label}
          </span>
        </p>
      </div>

      {/* Sélecteur de devise */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate('/settings/usage')}
          className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2"
        >
          {t('subscription.viewUsageLink')}
        </button>
        <CurrencySelector variant="compact" />
      </div>

      {/* Bannières de statut */}
      {!statusLoading && (
        <>
          {/* Bannière: Downgrade programmé */}
          {pendingDowngrade && (
            <StatusBanner
              type="downgrade-scheduled"
              data={{
                target_plan: pendingDowngrade.target_plan,
                effective_date: new Date(pendingDowngrade.effective_date).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })
              }}
              onCancel={handleCancelDowngrade}
            />
          )}

          {/* Bannière: OVER_QUOTA */}
          {organizationStatus?.organization?.status === 'OVER_QUOTA' && (
            <StatusBanner
              type="over-quota"
              data={{
                current_plan: subscription?.plan,
                overages: organizationStatus.organization.over_quota_details?.overages || []
              }}
            />
          )}

          {/* Bannière: Trial expiré */}
          {subscription?.status === 'trial_expired' && (
            <StatusBanner type="trial-expired" />
          )}

          {/* Bannière: Read-only (SUSPENDED ou CANCELLED) */}
          {(organizationStatus?.organization?.status === 'SUSPENDED' ||
            organizationStatus?.organization?.status === 'CANCELLED') && (
            <StatusBanner type="read-only" />
          )}
        </>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {plans.map((plan) => {
          const isCurrent = currentPlan === plan.code;
          const isPopular = plan.code === 'standard';
          const isBusiness = plan.code === 'business';

          return (
            <div
              key={plan.code}
              className={`bg-white rounded-xl border-2 p-6 relative flex flex-col ${
                isPopular
                  ? 'border-blue-500 shadow-lg'
                  : isCurrent
                  ? 'border-green-500 shadow-lg'
                  : 'border-gray-200'
              }`}
            >
              {isPopular && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="bg-blue-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                    {t('subscription.badges.popular')}
                  </div>
                </div>
              )}
              {isCurrent && (
                <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2 z-10">
                  <div className="bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg">
                    <FiCheck size={14} />
                    {t('subscription.badges.current')}
                  </div>
                </div>
              )}

              <h3 className="text-xl font-bold text-gray-900 mb-2 mt-4">{plan.name}</h3>
              <p className="text-sm text-gray-600 mb-4 flex-grow">{plan.description}</p>

              <div className="mb-6">
                <span className="text-4xl font-bold text-gray-900">{formatPrice(plan)}</span>
                <span className="text-gray-500">{getPriceSuffix(plan)}</span>
                {plan.display_price && plan.price_monthly_cents && plan.price_monthly_cents > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    ou {formatAmount(((plan.price_monthly_cents / 100) * 12 * 0.8), currency)}/an (-20%)
                  </div>
                )}
              </div>

              {/* Quotas */}
              {plan.limits && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs font-semibold text-gray-500 mb-2">LIMITES</p>
                  <ul className="space-y-1.5 text-sm text-gray-700">
                    <li>
                      <span className="font-medium">Projets:</span>{' '}
                      {plan.limits.projects === -1 ? 'Illimités' : plan.limits.projects}
                    </li>
                    <li>
                      <span className="font-medium">Architectures:</span>{' '}
                      {plan.limits.architectures === -1 ? 'Illimitées' : plan.limits.architectures}
                    </li>
                    <li>
                      <span className="font-medium">Déploiements/mois:</span>{' '}
                      {plan.limits.deployments_per_month === -1 ? 'Illimités' : plan.limits.deployments_per_month}
                    </li>
                  </ul>
                </div>
              )}

              <ul className="space-y-3 mb-6 flex-grow">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                    <FiCheck className="text-green-600 mt-0.5 flex-shrink-0" size={16} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan.code)}
                disabled={isCurrent}
                className={`w-full py-2 rounded-lg font-medium transition ${
                  isBusiness
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : isPopular
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : isCurrent
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {isCurrent ? t('subscription.buttons.currentPlan') : isBusiness ? t('subscription.buttons.contactUs') : t('subscription.buttons.choosePlan')}
              </button>
            </div>
          );
        })}
      </div>

      {/* Payment Method */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <FiCreditCard className="text-gray-600" size={24} />
          <h3 className="text-lg font-bold text-gray-900">{t('subscription.paymentMethod.title')}</h3>
        </div>
        {currentPlan === 'free' ? (
          <div>
            <p className="text-gray-600 mb-4">{t('subscription.paymentMethod.noCardRequired')}</p>
            <p className="text-sm text-gray-500">
              {t('subscription.paymentMethod.upgradeHint')}
            </p>
          </div>
        ) : (
          <div>
            <p className="text-gray-600 mb-4">
              {t('subscription.paymentMethod.manageDescription')}
            </p>
            <button
              onClick={handleOpenCustomerPortal}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-medium"
            >
              {t('subscription.paymentMethod.manageButton')}
            </button>
          </div>
        )}
      </div>

      {/* Billing History */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <FiCalendar className="text-gray-600" size={24} />
            <h3 className="text-lg font-bold text-gray-900">{t('subscription.billingHistory.title')}</h3>
          </div>
        </div>

        {currentPlan === 'free' ? (
          <p className="text-gray-500 text-center py-8">{t('subscription.billingHistory.noInvoices')}</p>
        ) : invoicesLoading ? (
          <div className="text-center py-8">
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-gray-500 text-center py-8">{t('subscription.billingHistory.noInvoices')}</p>
        ) : (
          <div className="space-y-3">
            {invoices.map((invoice) => {
              const date = new Date(invoice.created * 1000);
              const formattedDate = date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              });
              const amount = (invoice.amount / 100).toFixed(2);
              const statusMap: { [key: string]: { label: string; color: string } } = {
                paid: { label: t('subscription.billingHistory.status.paid'), color: 'green' },
                open: { label: t('subscription.billingHistory.status.open'), color: 'yellow' },
                draft: { label: t('subscription.billingHistory.status.draft'), color: 'gray' },
                void: { label: t('subscription.billingHistory.status.void'), color: 'red' },
                uncollectible: { label: t('subscription.billingHistory.status.uncollectible'), color: 'red' }
              };
              const status = statusMap[invoice.status] || { label: invoice.status, color: 'gray' };

              return (
                <div key={invoice.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                  <div>
                    <p className="font-medium text-gray-900">{invoice.number}</p>
                    <p className="text-sm text-gray-500">{formattedDate}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-medium text-gray-900">{amount} {invoice.currency.toUpperCase()}</span>
                    <span className={`px-3 py-1 bg-${status.color}-100 text-${status.color}-700 text-xs font-medium rounded-full`}>
                      {status.label}
                    </span>
                    {invoice.invoice_pdf && (
                      <a
                        href={invoice.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 p-2"
                      >
                        <FiDownload size={18} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal - Flow d'annulation amélioré */}
      {showCancellationFlow && previewData && (
        <CancellationFlowModal
          isOpen={showCancellationFlow}
          onClose={() => {
            setShowCancellationFlow(false);
            setPreviewData(null);
          }}
          onConfirmCancel={handleConfirmCancellation}
          onAcceptOffer={handleAcceptRetentionOffer}
          currentPlan={previewData.current_plan}
          effectiveDate={previewData.effective_date
            ? new Date(previewData.effective_date).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })
            : 'fin de la période actuelle'}
          showRetentionOffers={true}
          discountPercent={20}
          pauseDurationDays={30}
        />
      )}

      {/* Modal A - Confirmation Downgrade Générique (legacy) */}
      {showDowngradeModal && previewData && (
        <DowngradeConfirmationModal
          isOpen={showDowngradeModal}
          onClose={() => {
            setShowDowngradeModal(false);
            setPreviewData(null);
          }}
          onConfirm={handleDowngradeToFree}
          currentPlan={previewData.current_plan as 'free' | 'standard' | 'entreprise'}
          targetPlan={previewData.target_plan as 'free' | 'standard' | 'entreprise'}
          effectiveDate={previewData.effective_date || new Date().toISOString()}
          isLoading={downgradingToFree}
        />
      )}

      {/* Modal B - Avertissement Dépassement Quotas */}
      {showWarningModal && previewData && (
        <DowngradeWarningModal
          isOpen={showWarningModal}
          onClose={() => {
            setShowWarningModal(false);
            setPreviewData(null);
          }}
          onConfirm={confirmDowngrade}
          targetPlan="free"
          effectiveDate={previewData.effective_date
            ? new Date(previewData.effective_date).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })
            : 'fin de la période actuelle'}
          quotaPrediction={previewData.quota_prediction}
        />
      )}

      {/* Stripe Checkout Modal */}
      {showStripeCheckout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setShowStripeCheckout(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition"
            >
              <FiX size={24} />
            </button>
            <div className="p-6">
              <StripeCheckout
                currentPlan={currentPlan as 'free' | 'standard' | 'entreprise'}
                suggestedPlan={suggestedPlan}
                currency={currency}
                onCancel={() => setShowStripeCheckout(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
